import {
  cloneElement,
  isValidElement,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Check, Copy, CircleAlert } from 'lucide-react';
import { useT } from '../i18n';
import { useDismiss } from '../lib/hooks';

/* ───────── Field: label, hint, error linked to the control (§9.4) ───────── */
export function Field({
  label,
  hint,
  error,
  optional,
  children,
  id: idProp,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  optional?: boolean;
  children: ReactElement;
  id?: string;
}) {
  const t = useT();
  const auto = useId();
  const id = idProp ?? auto;
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [hintId, errId].filter(Boolean).join(' ') || undefined;
  const control = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
      })
    : children;
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label} {optional && <span className="field__optional">({t('common.optional')})</span>}
      </label>
      {control}
      {hint && (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className="field__error" id={errId}>
          <CircleAlert size={16} aria-hidden="true" style={{ flex: 'none', marginTop: 2 }} />
          {error}
        </span>
      )}
    </div>
  );
}

export function Checkbox({ label, description, box, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; description?: ReactNode; box?: boolean }) {
  return (
    <label className={['check', box && 'check--box'].filter(Boolean).join(' ')}>
      <input type="checkbox" {...rest} />
      <span>
        <span>{label}</span>
        {description && <span className="muted small" style={{ display: 'block' }}>{description}</span>}
      </span>
    </label>
  );
}

export function Switch({ label, description, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; description?: ReactNode }) {
  const id = useId();
  return (
    <div className="row row--between" style={{ flexWrap: 'nowrap', minHeight: 48 }}>
      <label htmlFor={id} style={{ cursor: 'pointer' }}>
        <span style={{ fontWeight: 500 }}>{label}</span>
        {description && <span className="muted small" style={{ display: 'block' }}>{description}</span>}
      </label>
      <input id={id} type="checkbox" role="switch" className="switch" {...rest} />
    </div>
  );
}

/* ───────── Segmented control ───────── */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: ReactNode }>;
  label: string;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const onKey = (e: ReactKeyboardEvent, i: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + options.length) % options.length;
    onChange(options[next].value);
    refs.current[next]?.focus();
  };
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((o, i) => (
        <button
          key={o.value}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          tabIndex={value === o.value ? 0 : -1}
          onClick={() => onChange(o.value)}
          onKeyDown={(e) => onKey(e, i)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ───────── Copy button: copies the raw value, shows "Gekopieerd" for 1.5 s (§8.3) ───────── */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for browsers without async clipboard permission.
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    ta.remove();
    return ok;
  }
}

export function CopyButton({ value, label, size = 'sm', ariaLabel }: { value: string; label?: string; size?: 'sm' | 'md'; ariaLabel?: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    if (await copyText(value)) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }
  };
  return (
    <button
      type="button"
      className={`btn btn--secondary ${size === 'sm' ? 'btn--sm' : ''}`}
      onClick={onClick}
      aria-label={copied ? t('common.copied') : ariaLabel ?? label ?? t('common.copy')}
      style={{ minWidth: 112 }}
    >
      {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
      <span aria-live="polite">{copied ? t('common.copied') : label ?? t('common.copy')}</span>
    </button>
  );
}

export function CopyField({ value, label }: { value: string; label?: string }) {
  return (
    <div className="copy-field">
      <code title={value}>{value}</code>
      <CopyButton value={value} ariaLabel={label} />
    </div>
  );
}

/* ───────── Dropdown menu ───────── */
export function Menu({
  trigger,
  children,
  align = 'right',
  label,
  width,
}: {
  trigger: (p: { open: boolean; toggle: () => void; props: Record<string, unknown> }) => ReactNode;
  children: (close: () => void) => ReactNode;
  align?: 'left' | 'right';
  label: string;
  width?: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuId = useId();
  useDismiss(ref, open, () => setOpen(false));
  const close = () => setOpen(false);
  const onMenuKey = (e: ReactKeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    const idx = items.indexOf(document.activeElement as HTMLElement);
    const next = e.key === 'ArrowDown' ? (idx + 1) % items.length : (idx - 1 + items.length) % items.length;
    items[next]?.focus();
  };
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {trigger({
        open,
        toggle: () => setOpen((o) => !o),
        props: { 'aria-haspopup': 'menu', 'aria-expanded': open, 'aria-controls': open ? menuId : undefined },
      })}
      {open && (
        <div
          id={menuId}
          className="popover"
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKey}
          style={{ [align]: 0, top: 'calc(100% + 6px)', width }}
        >
          {children(close)}
        </div>
      )}
    </div>
  );
}
